import React, { type ReactNode } from "react";
import Image, { type StaticImageData } from "next/image";
import { TextTitle } from "ui/text";
import { CenteredLoopSlider } from "ui/slider";
import { Box, type SpaceProps, Text } from "@chakra-ui/react";
import { horizontalContainerPadding } from "ui/container";
import { Link } from "ui/link";
import { Section } from "components/section";
import { remCalc } from "theme/utils";
import Balancer from "react-wrap-balancer";
import { useSwiperSlide } from "swiper/react";
import { cardHoverTranslation } from "utils";
import iterable from "../../../public/assets/industry-leader-awards/iterable.png";
import snowflake from "../../../public/assets/industry-leader-awards/snowflake.png";
import g2Leader from "../../../public/assets/industry-leader-awards/g2-leader.png";
import redpoint from "../../../public/assets/industry-leader-awards/redpoint.png";
import forbesBestStartupEmployers from "../../../public/assets/industry-leader-awards/forbes.png";
import fivetran from "../../../public/assets/industry-leader-awards/fivetran.png";
import gartner from "../../../public/assets/industry-leader-awards/gartner.png";
import g2BestEstRoi from "../../../public/assets/industry-leader-awards/g2-best-est-roi.png";
import databricks from "../../../public/assets/industry-leader-awards/databricks.png";
import databricksLight from "../../../public/assets/industry-leader-awards/databricks-light.png";
import retailTech from "../../../public/assets/industry-leader-awards/retail-tech.png";

const awards: AwardCardProps[] = [
  {
    title: "Databricks Invests in Hightouch",
    url: "https://www.databricks.com/blog/activating-data-lakehouse-databricks-ventures-invests-hightouch",
    image: {
      src: databricks,
      alt: "Databricks logo.",
    },
  },
  {
    title: (
      <>
        Technology Partner
        <br />
        of the Year
      </>
    ),
    url: "https://www.prnewswire.com/news-releases/iterable-names-hightouch-as-technology-partner-of-the-year-and-announces-smart-ingest-302133519.html?tc=eml_cleartime",
    image: {
      src: iterable,
      alt: "Iterable logo.",
    },
  },
  {
    title: (
      <>
        Data Cloud Product
        <br /> Partner of the Year
      </>
    ),
    url: "https://www.businesswire.com/news/home/20250603196641/en/Hightouch-Named-Snowflake-Marketers-Advertisers-Data-Cloud-Product-Partner-of-the-Year",
    image: {
      src: snowflake,
      alt: "Snowflake logo.",
    },
  },
  {
    title: (
      <>
        CDP Category
        <br />
        Leader
      </>
    ),
    url: "https://www.g2.com/categories/reverse-etl",
    image: {
      src: g2Leader,
      alt: "G2 Award: Reverse ETL Category Leader.",
    },
  },
  {
    title: (
      <>
        InfraRed 100 in Cloud <br />
        Data Infrastructure
      </>
    ),
    url: "https://www.prnewswire.com/news-releases/hightouch-named-to-redpoints-infrared-100-302175636.html",
    image: {
      src: redpoint,
      alt: "Redpoint logo.",
    },
  },
  {
    title: (
      <>
        America&rsquo;s Best
        <br />
        Startup Employers
      </>
    ),
    url: "/careers",
    image: {
      src: forbesBestStartupEmployers,
      alt: "Forbes 2025: America's best startup employers, powered by statista.",
    },
  },
  {
    title: (
      <>
        Ecosystem Partner
        <br />
        of the Year
      </>
    ),
    url: "https://hightouch.com/blog/fivetran-ecosystem-partner-of-the-year",
    image: {
      src: fivetran,
      alt: "Fivetran logo.",
    },
  },
  {
    title: "Cool Vendor in Marketing Data & Analytics",
    url: "https://hightouch.com/blog/gartner-cool-vendor",
    image: {
      src: gartner,
      alt: "Gartner logo.",
    },
  },
  {
    title: (
      <>
        Best Estimated
        <br />
        ROI
      </>
    ),
    url: "https://www.g2.com/products/hightouch/reviews",
    image: {
      src: g2BestEstRoi,
      alt: "G2 Award: Best Estimated ROI.",
    },
  },
  {
    title: (
      <>
        Retail & Consumer Goods <br />
        ISV Partner of the Year
      </>
    ),
    url: "https://www.businesswire.com/news/home/20250610695735/en/Hightouch-Awarded-2025-Databricks-Retail-and-Consumer-Goods-ISV-Partner-of-the-Year-at-Data-AI-Summit",
    image: {
      src: databricksLight,
      alt: "Databricks logo.",
    },
  },
  {
    title: "Omnichannel Analytics Solution of the Year",
    url: "https://www.businesswire.com/news/home/20250424650372/en/Hightouch-Awarded-Omnichannel-Analytics-Solution-of-the-Year-By-RetailTech-Breakthrough",
    image: {
      src: retailTech,
      alt: "RetailTech breakthrough award 2025.",
    },
  },
];

type IndustryLeadersProps = {
  /**
   * Optionally hide the background color and use margin for spacing instead of padding.
   * Used to support rendering this component on legacy pages that have not be redesigned.
   *
   * @todo remove this prop after all pages using this component are updated
   */
  hideBg?: boolean; // eslint-disable-line react/boolean-prop-naming
  title?: string;
} & SpaceProps;

export function IndustryLeaderAwards({
  title = "Recognized as an industry leader by industry leaders",
  hideBg,
  ...spaceProps
}: IndustryLeadersProps) {
  const bgColor = hideBg ? undefined : "new.gray.50";

  return (
    <Section
      fullWidth
      noContainerPx
      bg={bgColor}
      themeBgColor={bgColor}
      pb={{ base: "biggest", md: "huge" }}
      {...spaceProps}
    >
      <TextTitle
        align="center"
        mobileAlign="center"
        size="lg"
        title={
          <Balancer ratio={0.1} preferNative={false}>
            {title}
          </Balancer>
        }
        mb={{ base: "biggest", md: "huge" }}
        px={horizontalContainerPadding}
      />

      <CenteredLoopSlider controlsMt="0">
        {awards.map((award) => (
          <AwardCard key={award.url} {...award} />
        ))}
      </CenteredLoopSlider>
    </Section>
  );
}

type AwardCardProps = {
  title: ReactNode;
  url: string;
  image: {
    src: StaticImageData;
    alt: string;
  };
};

function AwardCard({ title, url, image }: AwardCardProps) {
  const { isDuplicate } = useSwiperSlide();

  return (
    /**
     * Parent element is necessary to avoid a layout shift when hovering over the bottom of the card.
     * See `cardHoverTranslation` for more details.
     */
    <Box
      aria-hidden={isDuplicate}
      className="group"
      h="100%"
      // Margins on outer container keep shadows visible on hover and don't extent the hover area.
      mt="small"
      pb="huge"
    >
      <Link
        tabIndex={isDuplicate ? -1 : undefined}
        href={url}
        target="_blank"
        variant="shadowCard"
        layerStyle="card.shadow.light"
        borderRadius="biggest"
        w={remCalc(312)}
        h="100%"
        mb={0}
        pos="relative"
        overflow="visible"
        transition="transform .2s ease"
        // Important: Do not animate box-shadow, it will have a major performance impact. Use a pseudo element instead.
        // Read more here: https://tobiasahlin.com/blog/how-to-animate-box-shadow/
        _after={{
          content: '""',
          position: "absolute",
          borderRadius: "biggest",
          inset: 0,
          boxShadow: "hoverTranslateY.card",
          opacity: 0,
          transition: "opacity .2s ease",
        }}
        _groupHover={{
          ...cardHoverTranslation,
          _after: {
            opacity: { base: "none", md: 1 },
          },
        }}
      >
        <Box layerStyle="card.media" h={remCalc(240)}>
          <Image fill src={image.src} alt={image.alt} sizes="100%" style={{ objectFit: "cover" }} />
        </Box>

        <Text align="center" textStyle="body3" fontWeight="semibold">
          {title}
        </Text>
      </Link>
    </Box>
  );
}
